/**
 * 🎯 GAINS MONITOR PAGE - Wrapper com Ribbon e Dashboard
 * Componente que envolve o GainsMonitorDashboard com Ribbon de Ferramentas
 */

import React, { useState, useRef, useEffect } from 'react';
import GainsMonitorDashboard from './GainsMonitorDashboard';
import { MasterRibbon } from './MasterRibbon';
import { useGainsRibbon } from '../hooks/useGainsRibbon';
import KaizenForm from './KaizenForm';
import { LeanOfficeStatusBar } from './LeanOfficeStatusBar';
import { downloadGainsTemplate } from '../utils/downloadGainsTemplate';
import GainsImportService from '../services/gainsImportService';
import GainsExportService from '../services/gainsExportService';
import GainsMonitorService from '../services/gainsMonitorService';

interface GainsMonitorPageProps {
  organizationId: string;
  projectId?: string;
  stageId?: string;
  onGoHome?: () => void;
  onBackToProject?: () => void;
}

export const GainsMonitorPage: React.FC<GainsMonitorPageProps> = ({
  organizationId,
  projectId,
  stageId,
  onGoHome,
  onBackToProject
}) => {
  const [showKaizenForm, setShowKaizenForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [stats, setStats] = useState({
    totalKaizens: 0,
    activeKaizens: 0,
    sustainedRate: 0
  });
  
  const [gainsStats, setGainsStats] = useState({
    totalKaizens: 0,
    activeKaizens: 0,
    sustainedKaizens: 0,
    regressedKaizens: 0,
    totalLeadTimeReduction: 0,
    totalCostSavings: 0,
    sustainabilityRate: 0,
    activeAlerts: 0
  });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // Carregar estatísticas para o ribbon
  useEffect(() => {
    loadRibbonStats();
  }, [organizationId, refreshKey]);

  const loadRibbonStats = async () => {
    try {
      const [dashboardData, alerts] = await Promise.all([
        GainsMonitorService.getDashboardData(organizationId),
        GainsMonitorService.getAlerts({ resolved: false })
      ]);

      // Stats para o Ribbon (resumido)
      setStats({
        totalKaizens: dashboardData.total_kaizens || 0,
        activeKaizens: dashboardData.active_kaizens || 0,
        sustainedRate: dashboardData.avg_sustainability_rate || 0
      });

      // Stats para a StatusBar (completo)
      setGainsStats({
        totalKaizens: dashboardData.total_kaizens || 0,
        activeKaizens: dashboardData.active_kaizens || 0,
        sustainedKaizens: dashboardData.sustained_kaizens || 0,
        regressedKaizens: dashboardData.regressed_kaizens || 0,
        totalLeadTimeReduction: dashboardData.total_lead_time_reduction || 0,
        totalCostSavings: dashboardData.total_cost_savings || 0,
        sustainabilityRate: dashboardData.avg_sustainability_rate || 0,
        activeAlerts: alerts?.length || 0
      });

      setAlertCount(alerts?.length || 0);
    } catch (error) {
      console.error('Erro ao carregar estatísticas do ribbon:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshKey(prev => prev + 1);
    await loadRibbonStats();
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const handleKaizenSuccess = () => {
    setShowKaizenForm(false);
    setRefreshKey(prev => prev + 1);
    loadRibbonStats();
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadGainsTemplate();
    } catch (error: any) {
      alert(`Erro ao baixar template: ${error.message}`);
    }
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const result = await GainsImportService.importKaizensFromExcel(file, organizationId);

      if (result.success) {
        alert(
          `✅ Importação concluída!\n\n` +
          `Total: ${result.totalRows} linhas\n` +
          `Importados: ${result.importedCount} Kaizens\n` +
          `Erros: ${result.errors.length}\n\n` +
          (result.warnings.length > 0 ? result.warnings.join('\n') : '')
        );

        handleRefresh();
      } else {
        alert(
          `❌ Falha na importação!\n\n` +
          `Erros encontrados:\n${result.errors.join('\n')}`
        );
      }
    } catch (error: any) {
      alert(`Erro ao importar arquivo: ${error.message}`);
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await GainsExportService.exportToExcel(organizationId);
    } catch (error: any) {
      alert(`Erro ao exportar Excel: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await GainsExportService.exportToPDF(organizationId);
    } catch (error: any) {
      alert(`Erro ao exportar PDF: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportImage = async () => {
    if (!dashboardRef.current) {
      alert('Dashboard não está disponível para captura');
      return;
    }

    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(dashboardRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });

      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `GainsMonitor_Dashboard_${new Date().toISOString().slice(0, 10)}.png`;
          link.click();
          URL.revokeObjectURL(url);
        }
      });
    } catch (error: any) {
      alert(`Erro ao exportar imagem: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const statsWidget = (
    <div className="mr-1 flex items-center gap-1.5">
      <div className="rounded-[3px] border border-emerald-500/20 bg-emerald-500/8 px-2 py-1 text-[10px] font-semibold text-emerald-400">
        Kaizens: {stats.totalKaizens || 0}
      </div>
      <div className="rounded-[3px] border border-blue-500/20 bg-blue-500/8 px-2 py-1 text-[10px] font-semibold text-blue-400">
        Ativos: {stats.activeKaizens || 0}
      </div>
      <div className="rounded-[3px] border border-amber-500/20 bg-amber-500/8 px-2 py-1 text-[10px] font-semibold text-amber-400">
        Sustentação: {stats.sustainedRate?.toFixed(1) || 0}%
      </div>
    </div>
  );

  const ribbonConfig = useGainsRibbon({
    onGoHome,
    onBackToProject,
    onNewKaizen: () => setShowKaizenForm(true),
    onImportExcel: () => fileInputRef.current?.click(),
    onDownloadTemplate: handleDownloadTemplate,
    onExportExcel: handleExportExcel,
    onExportPDF: handleExportPDF,
    onExportImage: handleExportImage,
    onRefresh: handleRefresh,
    onAlerts: () => {
      // TODO: Implementar painel de alertas
      alert('Painel de alertas em desenvolvimento');
    },
    hasActiveFilters: false,
    alertCount,
    isRefreshing,
    statsWidget,
  });

  return (
    <div className="relative h-full w-full flex flex-col">
      {/* Ribbon - Barra de Ferramentas */}
      <MasterRibbon config={ribbonConfig} />

      {/* Dashboard Principal */}
      <div ref={dashboardRef} className="flex-1 overflow-auto pb-10">
        <GainsMonitorDashboard
          key={refreshKey}
          organizationId={organizationId}
          projectId={projectId}
        />
      </div>

      {/* Input oculto para upload de arquivo */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleImportExcel}
        className="hidden"
      />

      {/* Modal de Criação */}
      {showKaizenForm && projectId && (
        <KaizenForm
          projectId={projectId}
          organizationId={organizationId}
          onSuccess={handleKaizenSuccess}
          onCancel={() => setShowKaizenForm(false)}
        />
      )}

      {/* Status Bar - Rodapé */}
      <LeanOfficeStatusBar
        mode="gains"
        saveStatus="saved"
        gainsStats={gainsStats}
      />
    </div>
  );
};

export default GainsMonitorPage;
